import { API_ROUTES } from "@/constants/api";
import { QUERY_KEYS } from "@/constants/config";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createWorkspaceSchema,
  updateWorkspaceSettingsSchema,
  type Paginated,
  type Plan,
  type WorkspaceInvitationRow,
  type WorkspaceMemberWithProfile,
  type WorkspaceRole,
  type WorkspaceRow,
  type WorkspaceSettingsRow,
  type WorkspaceWithMembership,
} from "@bizy/core";

export interface WorkspaceAccessPayload {
  workspace: WorkspaceRow;
  settings: WorkspaceSettingsRow;
  role: WorkspaceRole;
  scopes: readonly string[];
  is_superuser: boolean;
}

export interface MembersPayload {
  members: WorkspaceMemberWithProfile[];
  invitations: WorkspaceInvitationRow[];
  plan: Plan;
  role: WorkspaceRole;
}

export const useWorkspaces = () =>
  useQuery({
    queryKey: [QUERY_KEYS.workspaces],
    queryFn: () => apiGet<Paginated<WorkspaceWithMembership>>(API_ROUTES.workspaces),
  });

export const useWorkspaceAccess = (workspaceId: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.workspace, workspaceId ?? "none"],
    enabled: Boolean(workspaceId),
    queryFn: () => apiGet<WorkspaceAccessPayload>(API_ROUTES.workspace(workspaceId as string)),
  });

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; slug: string; plan: Plan }) =>
      apiPost<WorkspaceRow>(API_ROUTES.workspaces, createWorkspaceSchema.parse(input)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.workspaces] }),
  });
};

export const useUpdateWorkspaceSettings = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<WorkspaceSettingsRow>) =>
      apiPatch<WorkspaceSettingsRow>(
        `${API_ROUTES.workspace(workspaceId)}/settings`,
        updateWorkspaceSettingsSchema.parse(patch),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.workspace, workspaceId] });
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.workspaces] });
    },
  });
};

export const useMembers = (workspaceId: string | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.members, workspaceId ?? "none"],
    enabled: Boolean(workspaceId),
    queryFn: () => apiGet<MembersPayload>(API_ROUTES.members(workspaceId as string)),
  });

export const useInviteMember = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: WorkspaceRole }) =>
      apiPost<WorkspaceInvitationRow>(API_ROUTES.members(workspaceId), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.members, workspaceId] }),
  });
};

export const useUpdateMemberRole = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { memberId: string; role: WorkspaceRole }) =>
      apiPatch(API_ROUTES.member(workspaceId, input.memberId), { role: input.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.members, workspaceId] }),
  });
};

export const useRemoveMember = (workspaceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      apiDelete<{ removed: boolean }>(API_ROUTES.member(workspaceId, memberId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.members, workspaceId] }),
  });
};
